import React, {
    Component,
    CSSProperties,
    MouseEvent,
    KeyboardEvent,
    FocusEvent,
    ChangeEvent,
} from "react";
import classnames from "classnames";

import { CloseIcon } from "~/core/icons";

import "./text-input.css";

export interface ITextInputProps {
    autoComplete?: boolean | string;
    autoFocus?: boolean;
    containerClassNames?: Array<string | Record<string, any>>;
    className?: string;
    clearable?: boolean;
    disabled?: boolean;
    errorText?: string;
    hasError?: boolean;
    id?: string;
    ignorePasswordManagers?: boolean;
    inputContainerLeftElements?: Array<JSX.Element>;
    inputContainerRightElements?: Array<JSX.Element>;
    inputCSS?: CSSProperties;
    reactKey?: string | number;
    labelTitle?: string;
    maxLength?: number;
    name?: string;
    onChange?: (value: string, formattedValue?: string, numericValue?: number) => void;
    onClear?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (event?: FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event?: FocusEvent<HTMLInputElement>) => void;
    password?: boolean;
    placeholderText?: string;
    preFixinputContainerRightElements?: boolean;
    readOnly?: boolean;
    required?: boolean;
    showTopLabel?: boolean;
    tabIndex?: number;
    testId?: string;
    title?: string;
    toolTip?: string;
    value?: string | number;
    visible?: boolean;
}

export interface ITextInputState {
    hasFocus: boolean;
    value: string | number;
    autoFocus: boolean;
}

export class TextInput<
    TProps extends ITextInputProps = ITextInputProps,
    TState extends ITextInputState = ITextInputState
> extends Component<TProps, TState> {
    private static REQUIRED_TEXT = "*";
    protected title: string;
    protected input: HTMLInputElement;

    public static defaultProps = {
        autoComplete: false,
        autoFocus: false,
        containerClassNames: [],
        className: null,
        clearable: false,
        disabled: false,
        errorText: "",
        hasError: false,
        ignorePasswordManagers: false,
        inputContainerLeftElements: [],
        inputContainerRightElements: [],
        labelTitle: "",
        onBlur: (): void => null,
        onClear: (): void => null,
        onFocus: (): void => null,
        onKeyDown: (): void => null,
        maxLength: 256,
        password: false,
        placeholderText: "Text Input",
        readOnly: false,
        required: false,
        showTopLabel: true,
        toolTip: null,
        value: "",
        visible: true,
    };

    constructor(props: TProps) {
        super(props);
        this.state = {
            hasFocus: this.props.autoFocus === true,
            value: props.value,
            autoFocus: props.autoFocus,
        } as TState;
    }

    componentDidMount(): void {
        this.valueToState(this.props.value, false);
    }

    componentDidUpdate(prevProps: TProps): void {
        if (this.props.value !== prevProps.value) {
            // Resync display state only; echoing a prop-driven change back through onChange can loop.
            this.valueToState(this.props.value, false);
        }
        if (this.props.autoFocus !== prevProps.autoFocus) {
            this.setState({
                autoFocus: this.props.autoFocus,
            });
        }
    }

    protected valueToState(value: string | number, notify: boolean = true): void {
        const stateAndFormattedAreEmpty = value === "" && !this.state.value;
        if (value !== this.state.value && !stateAndFormattedAreEmpty) {
            this.setState({ value }, () => {
                if (notify && this.props.onChange) {
                    this.props.onChange(value as string);
                }
            });
        }
    }

    protected onChange(event: ChangeEvent<HTMLInputElement>): void {
        this.valueToState(event.target.value, true);
    }

    protected getTitle(): string {
        return this.props.title;
    }

    #onFocus(event?: FocusEvent<HTMLInputElement>): void {
        if (this.input) {
            this.input.setSelectionRange(0, this.input.value.length);
        }
        event?.persist();
        this.setState({ hasFocus: true }, () => this.props.onFocus(event));
    }

    #onBlur(event?: FocusEvent<HTMLInputElement>): void {
        event?.persist();
        this.setState({ hasFocus: false }, () => {
            this.props.onBlur(event);
        });
    }

    blur(): void {
        if (this.input) {
            this.input.blur();
        } else {
            this.#onBlur();
        }
    }

    focus(): void {
        if (this.input) {
            this.input.focus();
        } else {
            this.#onFocus();
        }
    }

    #getRequiredStarEl(): JSX.Element {
        return (
            <span
                key="required-star"
                className={classnames("required-star", {
                    disabled: this.props.disabled,
                })}
            >
                {TextInput.REQUIRED_TEXT}
            </span>
        );
    }

    #getClearableBtn(): JSX.Element {
        const onClear = (evt: MouseEvent<HTMLSpanElement>): void => {
            evt.preventDefault();
            this.valueToState("");
            this.props.onClear();
        };
        return (
            <span key="clear" className="clear-btn-icon" onMouseDown={onClear}>
                <CloseIcon />
            </span>
        );
    }

    render(): JSX.Element {
        const containerDivClassNames = [
            this.props.containerClassNames,
            "form-input",
            "text-form-input",
            {
                focus: this.state.hasFocus,
                error: this.props.hasError,
                "no-error-text": this.props.hasError && !this.props.errorText,
            },
            this.props.className,
        ].filter(Boolean);

        const containerDivStyle: CSSProperties = {
            visibility: this.props.visible ? "visible" : "hidden",
        };

        const inputContainerClassNames = {
            "input-container": true,
            "required-input": this.props.required && this.state.hasFocus,
            disabled: this.props.disabled,
        };

        // Label Text (Top label, when focused)
        const labelVisible: boolean = Boolean(this.state.value) || this.state.hasFocus;
        const labelText: string = labelVisible ? this.props.placeholderText : "";

        const labelContClassNames = {
            "input-label-container": true,
            "label-visible": labelVisible,
        };

        const labelClassNames = {
            "disabled-text": this.props.disabled,
        };
        const labelStyle: CSSProperties = {
            visibility: labelVisible ? "visible" : "hidden",
        };

        const requiredElementClassNames: string = classnames("red-star", {
            disabled: this.props.disabled,
        });
        const requiredElement: JSX.Element | null =
            labelVisible && this.props.required ? (
                <span className={requiredElementClassNames}>{TextInput.REQUIRED_TEXT}</span>
            ) : null;

        // Chrome's built-in password manager offers to save anything typed into an
        // input[type=password] and ignores autocomplete/vendor opt-out attributes, so
        // opted-out password fields render as type=text with CSS character masking.
        const maskWithoutPasswordType = this.props.password && this.props.ignorePasswordManagers;

        const inputClassNames = {
            "disabled-text": this.props.disabled,
            enabled: !this.props.disabled,
            "hint-visible": labelVisible,
            focus: labelVisible,
            "masked-input": maskWithoutPasswordType,
        };

        const placeholderText = this.state.hasFocus ? "" : this.props.placeholderText;
        const autoComplete: string =
            typeof this.props.autoComplete === "boolean"
                ? this.props.autoComplete
                    ? "on"
                    : "off"
                : this.props.autoComplete;
        const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
            autoComplete,
            autoFocus: this.state.autoFocus,
            className: classnames(inputClassNames),
            disabled: this.props.disabled,
            maxLength: this.props.maxLength,
            onChange: (evt) => this.onChange(evt),
            onKeyDown: (evt) => this.props.onKeyDown(evt),
            onBlur: (evt) => this.#onBlur(evt),
            onFocus: (evt) => this.#onFocus(evt),
            placeholder: placeholderText,
            readOnly: this.props.readOnly,
            title:
                this.props.password || this.props.toolTip
                    ? ""
                    : ((this.getTitle() || this.state.value) as string),
            type: this.props.password && !maskWithoutPasswordType ? "password" : "text",
            value: this.state.value || "",
        };

        if (this.props.tabIndex) {
            inputProps.tabIndex = this.props.tabIndex;
        }

        if (this.props.name) {
            inputProps.name = this.props.name;
        }

        const testIdAttrs = this.props.testId ? { "data-testid": this.props.testId } : {};

        // Vendor opt-outs (1Password, LastPass, Bitwarden, Dashlane) for inputs that
        // password managers misread as login fields; autocomplete alone is widely
        // ignored once a password-type input is present.
        const passwordManagerIgnoreAttrs = this.props.ignorePasswordManagers
            ? {
                  "data-1p-ignore": "",
                  "data-bwignore": "",
                  "data-form-type": "other",
                  "data-lpignore": "true",
              }
            : {};

        const inputContainerLeftElements: JSX.Element[] =
            this.props.required && !(this.state.value || this.state.hasFocus)
                ? [...this.props.inputContainerLeftElements, this.#getRequiredStarEl()]
                : this.props.inputContainerLeftElements;

        const inputContainerRightElements: JSX.Element[] =
            this.props.clearable &&
            !this.props.disabled &&
            this.state.value &&
            this.state.value !== ""
                ? this.props.preFixinputContainerRightElements
                    ? [...this.props.inputContainerRightElements, this.#getClearableBtn()]
                    : [this.#getClearableBtn(), ...this.props.inputContainerRightElements]
                : this.props.inputContainerRightElements;

        return (
            <div
                className={classnames(containerDivClassNames)}
                style={containerDivStyle}
                key={this.props.reactKey}
            >
                {this.props.showTopLabel && (
                    <div
                        className={classnames(labelContClassNames)}
                        title={this.props.labelTitle}
                        key={`label-cont-${this.props.reactKey}`}
                    >
                        {requiredElement}
                        <label className={classnames(labelClassNames)} style={labelStyle}>
                            {labelText + ":"}
                        </label>
                    </div>
                )}
                <div
                    className={classnames(inputContainerClassNames)}
                    data-tooltipinput={this.props.toolTip ? this.props.toolTip : null}
                    key={`inner-cont-${this.props.reactKey}`}
                >
                    {inputContainerLeftElements}
                    <input
                        {...inputProps}
                        {...passwordManagerIgnoreAttrs}
                        {...testIdAttrs}
                        ref={(input) => (this.input = input)}
                        style={this.props.inputCSS}
                        key={`input-cont-${this.props.reactKey}`}
                    />
                    {inputContainerRightElements}
                </div>
                {this.props.hasError && this.props.errorText && (
                    <div className="input-label-container">
                        <label className="error-text" title={this.props.errorText}>
                            {this.props.errorText}
                        </label>
                    </div>
                )}
            </div>
        );
    }
}
